import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 18000-18999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const contractsErrors = {
  // 18xxx is a fresh block for the Contracts module (packages/server/contracts/) — split off
  // SOMETHING_WENT_WRONG. `errorMessages.noAccess`/`errorMessages.disabledModule`
  // (contracts/constants.ts) are shared across many contracts-module files, not just the forms
  // module call sites these two were first split from — reuse these same codes wherever else that
  // shared constant is read with a generic code, rather than minting per-file duplicates.
  //
  // `getContractDetails` (or an equivalent lookup) returned nothing — the contract is gone, or the
  // caller cannot see it.
  CONTRACT_NO_ACCESS: {
    errorCode: 18001,
    errorMessage: t("You Don’t Have Access to this Contract"),
    isDynamic: false,
  },
  // The contract exists and is visible, but has this specific module disabled
  // (`contractsVersions.modulesEnabled`).
  CONTRACT_MODULE_DISABLED: {
    errorCode: 18002,
    errorMessage: t("This module is disabled for this contract hence this action cannot be performed"),
    isDynamic: false,
  },
  // `errorMessages.lockedContract` — the contract (or the one being written to) is locked and not in
  // draft mode. Shared across contracts.service.ts, contractsWBS.service.ts,
  // contracts.generalService.ts, contracts.milestonesService.ts, contractsTimelineService.ts.
  CONTRACT_LOCKED: {
    errorCode: 18003,
    errorMessage: t("Contract is Locked and no further changes are allowed"),
    isDynamic: false,
  },
  // `errorMessages.lockedChildContract` (and near-identical file-local wordings of the same
  // concept) — the write is blocked because a locked CHILD contract still references the item.
  CONTRACT_CHILD_LOCKED: {
    errorCode: 18004,
    errorMessage: t("Some of the items are present in locked child contract hence no further changes are allowed"),
    isDynamic: false,
  },
  // `errorMessages.presentInForm` (and near-identical file-local wordings) — the item can't be
  // removed/unlinked/nested because it's already attached to a live form.
  CONTRACT_ITEM_PRESENT_IN_FORM: {
    errorCode: 18005,
    errorMessage: t("Some of the selected items are already added in forms and hence cannot be deleted"),
    isDynamic: false,
  },
  // `errorMessages.lockedParentContract` — the PARENT contract is locked, blocking a write on a
  // child contract's WBS item.
  CONTRACT_PARENT_LOCKED: {
    errorCode: 18006,
    errorMessage: t("Parent Contract is Locked hence no changes are allowed"),
    isDynamic: false,
  },
  // `errorMessages.boqItemsAmountExceeds` (and near-identical "Boq budget not available..."
  // wordings) — the attached BOQ items amount exceeds the allotted budget on a WBS item.
  CONTRACT_BOQ_AMOUNT_EXCEEDS_BUDGET: {
    errorCode: 18007,
    errorMessage: t("Attached BOQ items amount cannot be greater than the allotted budget amount to this WBS item"),
    isDynamic: false,
  },
  // `errorMessages.isInDraftMode` — the action is blocked while the contract is in draft mode
  // (distinct from CONTRACT_LOCKED, the opposite lifecycle state), and an orphan assignment on a
  // sub-contract in draft mode (contractsWBS.service.ts).
  CONTRACT_DRAFT_MODE: {
    errorCode: 18008,
    errorMessage: t("Contract is in draft mode, this action cannot be performed"),
    isDynamic: false,
  },
  // `errorMessages.inconsistentData`, genuine usage only (a requested custom quantity exceeds what's
  // available/consumed) — the internal-guard usages of the same constant stay SOMETHING_WENT_WRONG.
  CONTRACT_QUANTITY_EXCEEDS_AVAILABLE: {
    errorCode: 18009,
    errorMessage: t("The requested quantity exceeds what is available"),
    isDynamic: false,
  },
  // contracts.service.ts `linkEstimateToContract`: an estimate can only be linked at the parent
  // contract level, not a child contract.
  CONTRACT_ESTIMATE_PARENT_ONLY: {
    errorCode: 18010,
    errorMessage: t("Estimate can only be attached to the parent contract"),
    isDynamic: false,
  },
  // contracts.service.ts `saveContractAmount`: a manual budget amount can't be saved on a
  // WBS-based contract (WBS-based contracts derive their amount from WBS items instead).
  CONTRACT_MANUAL_BUDGET_NOT_ALLOWED_FOR_WBS: {
    errorCode: 18011,
    errorMessage: t("Manual budget amount cannot be saved for a WBS based contract"),
    isDynamic: false,
  },
  // The amount being saved exceeds what's available in the parent contract — shared concept between
  // contracts.service.ts's `saveContractAmount` and contractsWBS.service.ts's
  // `addBudgetAmountToWBSItem` (parent-contract WBS item amount).
  CONTRACT_AMOUNT_EXCEEDS_PARENT: {
    errorCode: 18012,
    errorMessage: t("Amount is greater than what is available in the parent contract"),
    isDynamic: false,
  },
  // contracts.generalService.ts `updateContractTasks`: this bulk task-column update can only be
  // performed from the top parent contract, not a child contract.
  CONTRACT_UPDATE_REQUIRES_TOP_PARENT: {
    errorCode: 18013,
    errorMessage: t("Update can only be done at the top parent contract level"),
    isDynamic: false,
  },
  // contractsWBS.service.ts `manageBoqViewItems`: a BOQ view item's type can only be changed on the
  // Prime contract.
  CONTRACT_TYPE_CHANGE_PRIME_ONLY: {
    errorCode: 18014,
    errorMessage: t("Type can only be changed in the Prime contract"),
    isDynamic: false,
  },
  // contractsWBS.service.ts `manageItemsBudgetAmount`: the amount is either less than the allotted
  // BOQ amount or already assigned to a nested contract's amount.
  CONTRACT_AMOUNT_INVALID_VS_NESTED: {
    errorCode: 18015,
    errorMessage: t(
      "Amount is either less than the allotted BOQ amount or already assigned to a nested contract's amount",
    ),
    isDynamic: false,
  },
  // contracts.serviceHelper.ts `linkBoqItemsToContract`: a requested BOQ link quantity exceeds the
  // available/estimate total for that item.
  CONTRACT_BOQ_LINK_QUANTITY_INVALID: {
    errorCode: 18016,
    errorMessage: t("The requested quantity is incorrect"),
    isDynamic: false,
  },
  // The contract has no live draft for the request: `revertContractDraftMirrorInSession` named a
  // version that is no live draft, `validateAndApplyDraftChangesIntoNewVersionInSession` was asked
  // to apply a direct draft while the contract is out of draft mode, or a change order was filed
  // (forms.service.ts) on a contract that is not in draft mode or has no open direct draft.
  CONTRACT_NO_DRAFT_VERSION: {
    errorCode: 18017,
    errorMessage: t("No draft version exists for this contract"),
    isDynamic: false,
  },
  // contracts.versions.ts `validateAndApplyDraftChangesIntoNewVersionInSession`: the targeted
  // contractVersionId is not a live draft version, or, on a change order's final approval, it is a
  // live draft that another form owns or that is marked rejected (so an approver can read this for
  // a draft that still is one).
  CONTRACT_VERSION_NOT_DRAFT: {
    errorCode: 18018,
    errorMessage: t("This is not a draft version"),
    isDynamic: false,
  },
  // "Form is not linked to a contract" — shared concept between contractsTimelineService.ts
  // (`getFormTasks`, `handleTasksInForm`) and contracts.SAP.ts (`triggerMbookCreationInSap`): the
  // form's `contractId` is missing.
  CONTRACT_FORM_NOT_LINKED: {
    errorCode: 18019,
    errorMessage: t("Form is not linked to a contract"),
    isDynamic: false,
  },
  // contractsTimelineService.ts `handleTasksInForm`: an actualEnd/progress value was supplied before
  // actualStart was set. 3 call sites, same concept.
  CONTRACT_TASK_START_DATE_REQUIRED: {
    errorCode: 18021,
    errorMessage: t("Please specify the actual start date first"),
    isDynamic: false,
  },
  // contractsTimelineService.ts `linkTasksToContracts`: no schedule is currently linked to this
  // contract, so tasks can't be linked/unlinked.
  CONTRACT_SCHEDULE_NOT_LINKED: {
    errorCode: 18023,
    errorMessage: t("No schedule is linked to this contract"),
    isDynamic: false,
  },
  // contracts.SAP.ts: a WBS/PO/M-Book item's mandatory custom-field template was left unfilled.
  // Same concept in both `syncContractController` and `triggerMbookCreationInSap`.
  CONTRACT_SAP_MANDATORY_FIELDS_MISSING: {
    errorCode: 18024,
    errorMessage: t("Mandatory custom fields are not filled"),
    isDynamic: false,
  },
  // contracts.SAP.ts `syncContractController`: a leaf WBS item is missing its SAP material code,
  // so the contract can't be synced.
  CONTRACT_SAP_ITEMS_NOT_CODED: {
    errorCode: 18025,
    errorMessage: t("Contract contains non-coded items, it cannot be synced"),
    isDynamic: false,
  },
  // contracts.SAP.ts `triggerMbookCreationInSap`: an M-Book form can only reference items from a
  // single WBS slot.
  CONTRACT_SAP_SINGLE_WBS_ITEM_ONLY: {
    errorCode: 18028,
    errorMessage: t("Only one WBS item is allowed in a form"),
    isDynamic: false,
  },
  // contracts.SAP.ts `triggerMbookCreationInSap`: an M-Book form can only reference items under a
  // single parent BOQ item.
  CONTRACT_SAP_SINGLE_PARENT_ITEM_ONLY: {
    errorCode: 18029,
    errorMessage: t("Only one parent item is allowed in a form"),
    isDynamic: false,
  },
  // contractsEstimate.service.ts `syncContractEstimateToLatestVersionInSession` — the "Sync Estimate"
  // action's own validation chain, split off SOMETHING_WENT_WRONG. All genuine, nameable business
  // rules a user can hit through the real Sync Estimate UI action.
  //
  // Estimate sync can only be performed at the parent contract level, not on a child contract.
  CONTRACT_ESTIMATE_SYNC_PARENT_ONLY: {
    errorCode: 18031,
    errorMessage: t("Estimate sync can only be performed on the parent contract"),
    isDynamic: false,
  },
  // The contract must be locked before an estimate sync can be performed.
  CONTRACT_ESTIMATE_SYNC_REQUIRES_LOCK: {
    errorCode: 18032,
    errorMessage: t("Estimate can only be synced after the contract is locked"),
    isDynamic: false,
  },
  // The parent contract must be synced to its estimate before a child contract can be.
  CONTRACT_ESTIMATE_SYNC_PARENT_FIRST: {
    errorCode: 18033,
    errorMessage: t("The estimate must be synced on the parent contract first"),
    isDynamic: false,
  },
  // No estimate is linked to this contract to sync from. 2 call sites (missing linked-estimate
  // embed, and missing versionId/estimateId within it), same user-facing concept.
  CONTRACT_ESTIMATE_NOT_LINKED: {
    errorCode: 18034,
    errorMessage: t("No estimate is linked to this contract for syncing"),
    isDynamic: false,
  },
  // The linked estimate no longer has any published version at all.
  CONTRACT_ESTIMATE_NO_PUBLISHED_VERSION: {
    errorCode: 18035,
    errorMessage: t("The linked estimate no longer has a published version"),
    isDynamic: false,
  },
  // The latest estimate version exists but is still a draft — publish it before syncing.
  CONTRACT_ESTIMATE_VERSION_DRAFT: {
    errorCode: 18036,
    errorMessage: t("The latest estimate version is still a draft. Publish it before syncing."),
    isDynamic: false,
  },
  // The linked estimate has no version newer than what the contract already synced.
  CONTRACT_ESTIMATE_NO_NEW_VERSION: {
    errorCode: 18037,
    errorMessage: t("No new estimate version is available to sync"),
    isDynamic: false,
  },
  // The contract has no estimate-linked BOQ items to sync in the first place.
  CONTRACT_ESTIMATE_NO_BOQ_ITEMS_TO_SYNC: {
    errorCode: 18038,
    errorMessage: t("No estimate-linked BOQ items were found on this contract to sync"),
    isDynamic: false,
  },
  // Syncing would violate downstream commitments already made against the current BOQ items
  // (accompanies the detailed `validationErrors` array).
  CONTRACT_ESTIMATE_SYNC_COMMITMENTS_VIOLATED: {
    errorCode: 18039,
    errorMessage: t("Cannot sync estimate — downstream commitments would be violated"),
    isDynamic: false,
  },
  // 18040+ is the contracts module's RESOURCE_NOT_FOUND (9003) split — a different generic code from
  // everything above, which came off SOMETHING_WENT_WRONG. Note what is deliberately NOT here: a
  // "contract not found" code. All 16 such sites reuse CONTRACT_NO_ACCESS (18001) instead, because
  // `getContractDetails` collapses "the contract is gone" with "you cannot see it", and every one of
  // those guards is immediately followed by an 18001 permission guard on the very same lookup.
  //
  // A `contractsVersions` row could not be resolved (a deleted, rejected or rolled-back version id),
  // or the version is not the contract's current one where a template slot or a file flag is written
  // (contracts.generalService.ts).
  // Distinct from CONTRACT_NO_DRAFT_VERSION (18017) and CONTRACT_VERSION_NOT_DRAFT (18018), which are
  // lifecycle states of a version that does exist.
  CONTRACT_VERSION_NOT_FOUND: {
    errorCode: 18040,
    errorMessage: t("Contract version not found"),
    isDynamic: false,
  },
  // `modifyContractItem`: the `contractsHasBoqItems` row being written does not exist in this contract
  // version. Deliberately NOT shared with CONTRACT_ITEM_BREAKUP_UNAVAILABLE below — that one is a read
  // with nothing to show, this is a write against a missing row, and the two send different copy.
  CONTRACT_BOQ_ITEM_NOT_FOUND: {
    errorCode: 18041,
    errorMessage: t("The BOQ item to modify does not exist in this contract version."),
    isDynamic: false,
  },
  // `getContractItemDetails`: the item resolved but has no quantity break-up to return.
  CONTRACT_ITEM_BREAKUP_UNAVAILABLE: {
    errorCode: 18046,
    errorMessage: t("No quantity break-up is available for the requested item."),
    isDynamic: false,
  },
  // A `contractsHasWBSItems` row does not exist in this contract version. 2 call sites, identical
  // wording.
  CONTRACT_WBS_ITEM_NOT_FOUND: {
    errorCode: 18042,
    errorMessage: t("The WBS item does not exist in this contract version"),
    isDynamic: false,
  },
  // An `InspectionAndProgress` form row could not be resolved from a contract-side lookup. 3 call
  // sites. There is no general FORM_NOT_FOUND in this registry: FORM_FLOW_NOT_FOUND (15010) is a flow
  // template, and CONTRACT_FORM_NOT_LINKED (18019) is a form that exists but carries no contractId.
  CONTRACT_FORM_NOT_FOUND: {
    errorCode: 18043,
    errorMessage: t("Form not found"),
    isDynamic: false,
  },
  // A `mileStones` row could not be resolved.
  CONTRACT_MILESTONE_NOT_FOUND: {
    errorCode: 18044,
    errorMessage: t("Milestone not found"),
    isDynamic: false,
  },
  // A `contractsHasTasks` row does not exist on this contract version. Deliberately not TASK_NOT_FOUND
  // (16005) — that is the tasks-module row, a different collection from a contract's task link.
  CONTRACT_TASK_NOT_FOUND: {
    errorCode: 18045,
    errorMessage: t("Task does not exist on this contract version"),
    isDynamic: false,
  },
  // 18047-18067 completes the contracts module's split. CONTRACT_INCORRECT_DATA (6 sites) and
  // CONTRACT_SAP_INCORRECT_DATA (2) are umbrella codes by construction: the call sites themselves
  // ship one sentence for several different validation failures. One further site reused
  // CONTRACT_NO_ACCESS, matching the anti-enumeration mask chosen for that exact string earlier.
  CONTRACT_SAP_INCORRECT_DATA: {
    errorCode: 18047,
    errorMessage: t("Incorrect Data cannot be synced"),
    isDynamic: false,
  },
  CONTRACT_SAP_FORM_NO_REFERENCE_NUMBER: {
    errorCode: 18048,
    errorMessage: t("Form has no reference number"),
    isDynamic: false,
  },
  CONTRACT_SAP_FORM_ITEM_NOT_SYNCABLE: {
    errorCode: 18049,
    errorMessage: t("Form contains an item that cannot be synced"),
    isDynamic: false,
  },
  CONTRACT_UNEXPECTED_TRY_AGAIN: {
    errorCode: 18050,
    errorMessage: t("Something went wrong please try again"),
    isDynamic: false,
  },
  CONTRACT_ITEM_NO_BREAK_UP_DETAILS: {
    errorCode: 18051,
    errorMessage: t("No break-up details are available for the requested item."),
    isDynamic: false,
  },
  CONTRACT_MILESTONE_CREATE_FAILED: {
    errorCode: 18052,
    errorMessage: t("Failed to create the milestone."),
    isDynamic: false,
  },
  CONTRACT_MILESTONES_REQUIRE_CONTRACT_FORM: {
    errorCode: 18053,
    errorMessage: t("Milestones are only supported in contract based forms"),
    isDynamic: false,
  },
  CONTRACT_INCORRECT_DATA: {
    errorCode: 18054,
    errorMessage: t("Incorrect Data"),
    isDynamic: false,
  },
  CONTRACT_COMPLETION_DATE_REQUIRES_PROGRESS_FORM: {
    errorCode: 18055,
    errorMessage: t("Updating the completion date is only available in progress updates form"),
    isDynamic: false,
  },
  CONTRACT_CREATE_FAILED: {
    errorCode: 18056,
    errorMessage: t("Failed to create the Contract."),
    isDynamic: false,
  },
  CONTRACT_NONE_DELETED: {
    errorCode: 18057,
    errorMessage: t("No contract was deleted."),
    isDynamic: false,
  },
  CONTRACT_UNSUPPORTED_ENTITY: {
    errorCode: 18058,
    errorMessage: t("Unsupported entity"),
    isDynamic: false,
  },
  CONTRACT_DRAFT_PROMOTE_FAILED: {
    errorCode: 18059,
    errorMessage: t("The draft could not be promoted into a new Contract version."),
    isDynamic: false,
  },
  CONTRACT_FORM_HAS_NO_TASKS: {
    errorCode: 18060,
    errorMessage: t("This form is not based on a budget, progress update or inspection, so it has no tasks."),
    isDynamic: false,
  },
  CONTRACT_NO_TASK_CONFIGURATION: {
    errorCode: 18061,
    errorMessage: t("No task configuration supplied"),
    isDynamic: false,
  },
  CONTRACT_NO_QUANTITY_SUPPLIED: {
    errorCode: 18062,
    errorMessage: t("No quantity supplied"),
    isDynamic: false,
  },
  CONTRACT_SCHEDULE_NO_VERSION_TO_LINK: {
    errorCode: 18063,
    errorMessage: t("Schedule has no version to link"),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEMS_TO_DELETE_UNREADABLE: {
    errorCode: 18064,
    errorMessage: t("The BOQ items selected for deletion could not be read from this contract version."),
    isDynamic: false,
  },
  CONTRACT_WBS_AMOUNTS_UPDATE_FAILED: {
    errorCode: 18065,
    errorMessage: t("The contract's WBS item amounts could not be updated."),
    isDynamic: false,
  },
  CONTRACT_BOQ_VIEW_ACTION_FAILED: {
    errorCode: 18066,
    errorMessage: t("The BOQ view action could not be completed."),
    isDynamic: false,
  },
  CONTRACT_WBS_BUDGET_CHANGE_NOT_ACKNOWLEDGED: {
    errorCode: 18067,
    errorMessage: t("The WBS budget amount change was not acknowledged by the database."),
    isDynamic: false,
  },
  // Off INSUFFICIENT_PERMISSION (7012): a depth limit is a validation rule, not a permission check.
  CONTRACT_NESTING_LIMIT_EXCEEDED: {
    errorCode: 18068,
    errorMessage: t("Nesting more than 4 level is not allowed"),
    isDynamic: false,
  },
  // 18069-18099 is the contracts module's NO_CHANGES_APPLIED (9005) split. Contracts is by far
  // the heaviest user of 9005: its writes are idempotent-by-design and report "nothing changed"
  // with a sentence naming exactly what did not change. Several pairs are deliberate: the
  // "already attached" guard and the "none of the selected ... so nothing was removed" result are
  // different facts, so each keeps its own code.
  CONTRACT_TEMPLATE_ALREADY_SET: {
    errorCode: 18069,
    errorMessage: t("Contract version not found, or the template was already set."),
    isDynamic: false,
  },
  CONTRACT_TEMPLATE_UNCHANGED: {
    errorCode: 18070,
    errorMessage: t("Contract version not found, or the template was unchanged."),
    isDynamic: false,
  },
  CONTRACT_CONFIGURATION_UNCHANGED: {
    errorCode: 18071,
    errorMessage: t("The contract configuration was not updated."),
    isDynamic: false,
  },
  CONTRACT_FILE_NOT_IN_VERSION: {
    errorCode: 18072,
    errorMessage: t("File not found in this contract version."),
    isDynamic: false,
  },
  CONTRACT_MILESTONE_UNCHANGED: {
    errorCode: 18073,
    errorMessage: t("The milestone already carries these values, so nothing was updated."),
    isDynamic: false,
  },
  CONTRACT_MILESTONES_NOT_IN_VERSION: {
    errorCode: 18074,
    errorMessage: t("None of the selected milestones exist in this contract version, so nothing was deleted."),
    isDynamic: false,
  },
  CONTRACT_MILESTONES_ALREADY_ON_FORM: {
    errorCode: 18075,
    errorMessage: t("The selected milestones are already attached to this form."),
    isDynamic: false,
  },
  CONTRACT_MILESTONES_NOT_ON_FORM: {
    errorCode: 18076,
    errorMessage: t("None of the selected milestones are attached to this form, so nothing was removed."),
    isDynamic: false,
  },
  CONTRACT_MILESTONE_AVAILABLE_AMOUNT_UPDATE_FAILED: {
    errorCode: 18077,
    errorMessage: t("The milestone's available amount could not be updated for this contract version."),
    isDynamic: false,
  },
  CONTRACT_UNCHANGED: {
    errorCode: 18078,
    errorMessage: t("Contract not found, or no field changed."),
    isDynamic: false,
  },
  CONTRACT_ALREADY_LOCKED: {
    errorCode: 18079,
    errorMessage: t("The Contract is already locked."),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEM_UNCHANGED: {
    errorCode: 18080,
    errorMessage: t("The BOQ item already carries these values, so nothing was changed."),
    isDynamic: false,
  },
  CONTRACT_MANUAL_BUDGET_BLOCKED_BY_ESTIMATE: {
    errorCode: 18081,
    errorMessage: t("Since contract is already linked with an estimate manual budget amount cannot be saved"),
    isDynamic: false,
  },
  CONTRACT_AMOUNT_BELOW_CHILD_USAGE: {
    errorCode: 18082,
    errorMessage: t("Amount cannot be lower than what is already being used in child contracts"),
    isDynamic: false,
  },
  CONTRACT_AMOUNT_LOCKED_IN_CHILD: {
    errorCode: 18083,
    errorMessage: t("Some amount is already being used in locked child contract(s) hence it cannot be removed"),
    isDynamic: false,
  },
  CONTRACT_MANUAL_BUDGET_ALREADY_CLEARED: {
    errorCode: 18084,
    errorMessage: t("The manual budget amount was already cleared."),
    isDynamic: false,
  },
  CONTRACT_ALREADY_IN_DRAFT_MODE: {
    errorCode: 18085,
    errorMessage: t("Contract is Already in Draft Mode"),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEM_TYPE_CHANGE_MATCHED_NOTHING: {
    errorCode: 18086,
    errorMessage: t("No BOQ item matched the item whose type is being changed, so nothing was converted."),
    isDynamic: false,
  },
  CONTRACT_ACTIVITIES_ALREADY_ON_FORM: {
    errorCode: 18087,
    errorMessage: t("The selected activities are already added to this form."),
    isDynamic: false,
  },
  CONTRACT_ACTIVITIES_NOT_ON_FORM: {
    errorCode: 18088,
    errorMessage: t("None of the selected activities are on this form, so nothing was removed."),
    isDynamic: false,
  },
  CONTRACT_ACTIVITY_PROGRESS_UNCHANGED: {
    errorCode: 18089,
    errorMessage: t("The activity is not on this form, or it already carries these progress details."),
    isDynamic: false,
  },
  CONTRACT_FORM_ITEM_QUANTITY_UNCHANGED: {
    errorCode: 18090,
    errorMessage: t("The item is not on this form, or its quantity already matches the submitted value."),
    isDynamic: false,
  },
  CONTRACT_TASKS_ALREADY_LINKED: {
    errorCode: 18091,
    errorMessage: t("Tasks are already linked to this contract"),
    isDynamic: false,
  },
  CONTRACT_TASK_ITEMS_IN_USE_BY_FORMS: {
    errorCode: 18092,
    errorMessage: t("Task items cannot be unlinked as they are already added in form(s)"),
    isDynamic: false,
  },
  CONTRACT_BOQ_LINK_UNCHANGED: {
    errorCode: 18093,
    errorMessage: t("No BOQ items were linked or unlinked."),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEMS_NOT_IN_VERSION_FOR_SAVE: {
    errorCode: 18094,
    errorMessage: t("None of the supplied BOQ items belong to this contract version, so nothing was saved."),
    isDynamic: false,
  },
  CONTRACT_WBS_ITEMS_ALREADY_LINKED: {
    errorCode: 18095,
    errorMessage: t("The selected WBS items are already linked to this contract."),
    isDynamic: false,
  },
  CONTRACT_WBS_ITEMS_NOT_LINKED: {
    errorCode: 18096,
    errorMessage: t("None of the selected WBS items are linked to this contract, so nothing was unlinked."),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEMS_NOT_IN_VERSION_FOR_DELETE: {
    errorCode: 18097,
    errorMessage: t("None of the selected BOQ items exist in this contract version, so nothing was deleted."),
    isDynamic: false,
  },
  CONTRACT_NO_ORPHAN_BOQ_ITEM: {
    errorCode: 18098,
    errorMessage: t("No orphan BOQ item matched this contract version."),
    isDynamic: false,
  },
  CONTRACT_AMOUNT_BELOW_FORM_CONSUMPTION: {
    errorCode: 18099,
    errorMessage: t("Amount cannot be less that what is already being consumed through forms"),
    isDynamic: false,
  },
  // 18101-18102 replace the `errorMessages.inconsistentData` / `.boqItemsActionMismatch` constants
  // (contracts/constants.ts). Both were on SOMETHING_WENT_WRONG at all 6 of their call sites; they
  // were missed by earlier passes because those sites use the ASSIGNMENT form
  // (`output.errorCode = ...`) rather than an object literal. Shared constants, so the sites keep
  // their sentence and only the code changes.
  CONTRACT_INCONSISTENT_DATA_CANNOT_LINK: {
    errorCode: 18101,
    errorMessage: t("Inconsistent data cannot be linked"),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEMS_PAYLOAD_ACTION_MISMATCH: {
    errorCode: 18102,
    errorMessage: t("The BOQ items payload does not match the requested action"),
    isDynamic: false,
  },
  // The last INSUFFICIENT_PERMISSION (7012) site carrying its own sentence.
  CONTRACT_PARENT_NO_WRITE_PERMISSION: {
    errorCode: 18103,
    errorMessage: t("You Dont have write permission in one of the parent contract"),
    isDynamic: false,
  },
  // 18104-18110 came off VALIDATION_FAILED (9004).
  CONTRACT_MANDATORY_CUSTOM_FIELDS_MISSING: {
    errorCode: 18104,
    errorMessage: t("Contract Mandatory custom fields are missing"),
    isDynamic: false,
  },
  CONTRACT_PARENT_ITEM_CUSTOM_FIELDS_MISSING: {
    errorCode: 18105,
    errorMessage: t("Parent items mandatory custom fileds are missing"),
    isDynamic: false,
  },
  CONTRACT_CHILD_ITEM_CUSTOM_FIELDS_MISSING: {
    errorCode: 18106,
    errorMessage: t("Child items mandatory custom fileds are missing"),
    isDynamic: false,
  },
  CONTRACT_FORM_ITEM_PARENT_MISSING: {
    errorCode: 18107,
    errorMessage: t("Form contains an item whose parent item is missing"),
    isDynamic: false,
  },
  CONTRACT_MODULE_NAME_INVALID: {
    errorCode: 18108,
    errorMessage: t("Invalid module name"),
    isDynamic: false,
  },
  CONTRACT_FORM_NO_MILESTONE_SOURCE: {
    errorCode: 18109,
    errorMessage: t("This form has no milestone source (budget, inspections, progress updates or tasks)."),
    isDynamic: false,
  },
  // 18115-18125: each carries VERBATIM the sentence its contracts call site used to send inline.
  CONTRACT_SAP_WBS_ONLY: {
    errorCode: 18115,
    errorMessage: t("Sync is only available for WBS based contracts"),
    isDynamic: false,
  },
  CONTRACT_MILESTONE_COMPLETION_UNCHANGED: {
    errorCode: 18116,
    errorMessage: t("The milestone is not on this form, or its completion date already matches the requested one."),
    isDynamic: false,
  },
  CONTRACT_BOQ_ITEMS_ALREADY_LINKED: {
    errorCode: 18117,
    errorMessage: t("These BOQ items are already linked to this contract version."),
    isDynamic: false,
  },
  CONTRACT_TASK_ACTUAL_START_AFTER_END: {
    errorCode: 18118,
    errorMessage: t("ActualStart Date cannot be greater than ActualEnd Date"),
    isDynamic: false,
  },
  CONTRACT_TASK_ACTUAL_END_BEFORE_START: {
    errorCode: 18119,
    errorMessage: t("ActualEnd Date cannot be smaller than ActualStart Date"),
    isDynamic: false,
  },
  CONTRACT_FORM_PROGRESS_UPDATES_ONLY: {
    errorCode: 18120,
    errorMessage: t("Only Progress updates forms can be filled with these details"),
    isDynamic: false,
  },
  CONTRACT_TIMELINE_BLOCKED_BY_LOCKED_CHILD: {
    errorCode: 18121,
    errorMessage: t("This contract already have one or more locked child contracts hence timeline cannot be unlinked"),
    isDynamic: false,
  },
  CONTRACT_SCHEDULE_UNLINK_NOT_APPLICABLE: {
    errorCode: 18122,
    errorMessage: t("The contract is locked, or it has no schedule linked to unlink."),
    isDynamic: false,
  },
  CONTRACT_WBS_ITEM_ALREADY_IN_HIERARCHY: {
    errorCode: 18123,
    errorMessage: t(
      "Selected item or one of its child item is already added in WBS item of this or one of the parent contract hence selected items cannot be added",
    ),
    isDynamic: false,
  },
  CONTRACT_NEST_BLOCKED_BY_LOCKED_CHILD: {
    errorCode: 18124,
    errorMessage: t("Some of the items are already present in locked child contract and hence cannot be nested"),
    isDynamic: false,
  },
  CONTRACT_NEST_BLOCKED_BY_FORM: {
    errorCode: 18125,
    errorMessage: t(
      "Some Item(s) which you are trying to nest is/are already added in forms and hence cannnot be nested",
    ),
    isDynamic: false,
  },
  CONTRACT_MODULE_HAS_ITEMS: {
    errorCode: 18112,
    errorMessage: t(
      "There exists some {{itemType}} in either this contract or one of the child contracts hence this module cannot be disabled",
    ),
    isDynamic: true,
    dynamicParams: ["itemType"],
  },
  CONTRACT_MILESTONE_OPERATION_UNSUPPORTED: {
    errorCode: 18113,
    errorMessage: t('"{{operation}}" is not a supported milestone operation.'),
    isDynamic: true,
    dynamicParams: ["operation"],
  },
  CONTRACT_FORM_ACTIVITY_OPERATION_UNSUPPORTED: {
    errorCode: 18114,
    errorMessage: t('"{{operation}}" is not a supported form-activity operation.'),
    isDynamic: true,
    dynamicParams: ["operation"],
  },
  // 18126-18142: the per-item validation issues `contracts.versions.ts` reports in its
  // `validationErrors` list. They are entries inside a SUCCESSFUL response, not refusal
  // envelopes — each row renders on its own line in the contract-version warning modal.
  CONTRACT_BOQ_CHANGED_QTY_EXCEEDS_PARENT: {
    errorCode: 18126,
    errorMessage: t("Changed quantity for one or more Boq item is not available in parent contract"),
    isDynamic: false,
  },
  CONTRACT_BOQ_ADDED_QTY_EXCEEDS_PARENT: {
    errorCode: 18127,
    errorMessage: t("Added quantity for one or more Boq item is not available in parent contract"),
    isDynamic: false,
  },
  CONTRACT_WBS_CHANGED_AMOUNT_EXCEEDS_PARENT: {
    errorCode: 18128,
    errorMessage: t("Changed amount for one or more WBS item is not available in parent contract"),
    isDynamic: false,
  },
  CONTRACT_WBS_ADDED_AMOUNT_EXCEEDS_PARENT: {
    errorCode: 18129,
    errorMessage: t("Added amount for one or more WBS item is not available in parent contract"),
    isDynamic: false,
  },
  CONTRACT_TASKS_IN_SIBLING_CONTRACTS: {
    errorCode: 18130,
    errorMessage: t("Some Tasks are already present in sibling contracts and hence cannot be added in this contract"),
    isDynamic: false,
  },
  CONTRACT_BOQ_REVISED_QTY_BELOW_FORMS: {
    errorCode: 18131,
    errorMessage: t("One of the Boq items's revised quantity is smaller than the sum of what is added in forms"),
    isDynamic: false,
  },
  CONTRACT_BOQ_DELETE_BLOCKED_BY_FORMS: {
    errorCode: 18132,
    errorMessage: t("Some of the Boq items you are trying to delete is already added in one or many forms"),
    isDynamic: false,
  },
  CONTRACT_WBS_REVISED_AMOUNT_BELOW_FORMS: {
    errorCode: 18133,
    errorMessage: t("{{code}}'s revised amount is smaller than what is added in forms"),
    isDynamic: true,
    dynamicParams: ["code"],
  },
  CONTRACT_WBS_DELETE_BLOCKED_BY_FORMS: {
    errorCode: 18134,
    errorMessage: t("Some of the WBS items you are trying to delete is already added in one or many Forms"),
    isDynamic: false,
  },
  CONTRACT_TASKS_DELETE_BLOCKED_BY_FORMS: {
    errorCode: 18135,
    errorMessage: t("One or many tasks are already added in forms and hence cannot be deleted"),
    isDynamic: false,
  },
  CONTRACT_MILESTONE_REVISED_AMOUNT_BELOW_FORMS: {
    errorCode: 18136,
    errorMessage: t("Milestone {{title}}'s revised amount is smaller than what is added in forms"),
    isDynamic: true,
    dynamicParams: ["title"],
  },
  CONTRACT_MILESTONES_DELETE_BLOCKED_BY_FORMS: {
    errorCode: 18137,
    errorMessage: t("One or many milestones are already added in forms and hence cannot be deleted"),
    isDynamic: false,
  },
  CONTRACT_BOQ_REVISED_QTY_BELOW_CHILD_CONTRACTS: {
    errorCode: 18138,
    errorMessage: t(
      "One of the Boq items's revised quantity is smaller than the sum of what is allotted to child contracts",
    ),
    isDynamic: false,
  },
  CONTRACT_BOQ_DELETE_BLOCKED_BY_LOCKED_CHILD: {
    errorCode: 18139,
    errorMessage: t(
      "Some of the Boq items you are trying to delete is already added in one or many locked child contract(s)",
    ),
    isDynamic: false,
  },
  CONTRACT_WBS_REVISED_AMOUNT_BELOW_CHILD_CONTRACTS: {
    errorCode: 18140,
    errorMessage: t("{{code}}'s revised amount is smaller than what is allotted to child contracts"),
    isDynamic: true,
    dynamicParams: ["code"],
  },
  CONTRACT_WBS_DELETE_BLOCKED_BY_LOCKED_CHILD: {
    errorCode: 18141,
    errorMessage: t(
      "Some of the WBS items you are trying to delete is already added in one or many locked child contract(s)",
    ),
    isDynamic: false,
  },
  CONTRACT_TASKS_DELETE_BLOCKED_BY_LOCKED_CHILD: {
    errorCode: 18142,
    errorMessage: t("One or many tasks are already added in locked child contract and hence cannot be deleted"),
    isDynamic: false,
  },
  // The draft-already-exists refusal is two distinct sentences, so it is two codes rather than one
  // code with an interpolated fragment (contracts.versions.ts `createMirrorOfLatestContractVersion`).
  // The change-order one also answers a request naming a draft that only its change order may end:
  // /validateAndApplyDraftChangesIntoNewVersion for a change order's or a rejected draft, and
  // /revertContractDraftMirror for the draft of a change order still in its approval flow.
  CONTRACT_DRAFT_EXISTS_WITH_CHANGE_ORDER: {
    errorCode: 18143,
    errorMessage: t("A Draft version already exists for this contract associated with a change order form"),
    isDynamic: false,
  },
  CONTRACT_DRAFT_EXISTS: {
    errorCode: 18144,
    errorMessage: t("A Draft version already exists for this contract"),
    isDynamic: false,
  },
  // 18145-18160: the per-item issues `contractsEstimate.service.ts` reports when syncing a
  // contract to a newer estimate version. Where the old sentence interpolated an English word
  // (`${isInAnyWBS ? "WBS items" : "activities"}`) it is TWO codes, so each full sentence is its
  // own catalog key rather than a translated frame around an English fragment.
  CONTRACT_ESTIMATE_ITEM_DELETED_LINKED_WBS: {
    errorCode: 18145,
    errorMessage: t(
      "This item has been deleted in the new estimate version but is still linked to WBS items, child contracts, inspections, progress updates or bills.",
    ),
    isDynamic: false,
  },
  CONTRACT_ESTIMATE_ITEM_DELETED_LINKED_ACTIVITIES: {
    errorCode: 18146,
    errorMessage: t(
      "This item has been deleted in the new estimate version but is still linked to activities, child contracts, inspections, progress updates or bills.",
    ),
    isDynamic: false,
  },
  CONTRACT_ESTIMATE_ITEM_TYPE_CHANGED_WBS: {
    errorCode: 18147,
    errorMessage: t(
      "This item was a leaf in the linked estimate version but is now a header; it cannot be converted because the contract has already consumed it through WBS items, child contracts, inspections, progress updates or bills.",
    ),
    isDynamic: false,
  },
  CONTRACT_ESTIMATE_ITEM_TYPE_CHANGED_ACTIVITIES: {
    errorCode: 18148,
    errorMessage: t(
      "This item was a leaf in the linked estimate version but is now a header; it cannot be converted because the contract has already consumed it through activities, child contracts, inspections, progress updates or bills.",
    ),
    isDynamic: false,
  },
  CONTRACT_ESTIMATE_PARENT_HIERARCHY_CHANGED: {
    errorCode: 18149,
    errorMessage: t("Item has been moved under a different parent in the new estimate version."),
    isDynamic: false,
  },
  CONTRACT_ESTIMATE_RATE_CHANGED_WBS: {
    errorCode: 18150,
    errorMessage: t(
      "Item rate has changed from {{oldRate}} to {{newRate}} in the new estimate version. Rate change is not permitted because the item is already consumed by child contracts, forms, or WBS items, or the contract has milestones.",
    ),
    isDynamic: true,
    dynamicParams: ["oldRate", "newRate"],
  },
  CONTRACT_ESTIMATE_RATE_CHANGED_ACTIVITIES: {
    errorCode: 18151,
    errorMessage: t(
      "Item rate has changed from {{oldRate}} to {{newRate}} in the new estimate version. Rate change is not permitted because the item is already consumed by child contracts, forms, or activities, or the contract has milestones.",
    ),
    isDynamic: true,
    dynamicParams: ["oldRate", "newRate"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_TASKS: {
    errorCode: 18152,
    errorMessage: t(
      "New quantity {{newQuantity}} is less than quantity already linked to timeline activities ({{taskQuantity}}).",
    ),
    isDynamic: true,
    dynamicParams: ["newQuantity", "taskQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_CHILD: {
    errorCode: 18153,
    errorMessage: t(
      "New quantity {{newQuantity}} is less than quantity already linked to child contracts ({{childQuantity}}).",
    ),
    isDynamic: true,
    dynamicParams: ["newQuantity", "childQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_FORMS: {
    errorCode: 18154,
    errorMessage: t("New quantity {{newQuantity}} is less than quantity allocated in active forms ({{formQuantity}})."),
    isDynamic: true,
    dynamicParams: ["newQuantity", "formQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_BILLED: {
    errorCode: 18155,
    errorMessage: t("New quantity {{newQuantity}} is less than already billed quantity ({{billedQuantity}})."),
    isDynamic: true,
    dynamicParams: ["newQuantity", "billedQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_INSPECTED: {
    errorCode: 18156,
    errorMessage: t("New quantity {{newQuantity}} is less than already inspected quantity ({{inspectedQuantity}})."),
    isDynamic: true,
    dynamicParams: ["newQuantity", "inspectedQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_PROGRESS: {
    errorCode: 18157,
    errorMessage: t(
      "New quantity {{newQuantity}} is less than already used quantity in progress updates ({{usedQuantity}}).",
    ),
    isDynamic: true,
    dynamicParams: ["newQuantity", "usedQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_WBS: {
    errorCode: 18158,
    errorMessage: t("New quantity {{newQuantity}} is less than already consumed by WBS items ({{wbsQuantity}})."),
    isDynamic: true,
    dynamicParams: ["newQuantity", "wbsQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_TOTAL_EXTERNAL: {
    errorCode: 18159,
    errorMessage: t(
      "New quantity {{newQuantity}} is less than the combined quantity already claimed by child contracts, tasks and forms ({{totalQuantity}}).",
    ),
    isDynamic: true,
    dynamicParams: ["newQuantity", "totalQuantity"],
  },
  CONTRACT_ESTIMATE_QTY_BELOW_TOTAL_WBS: {
    errorCode: 18160,
    errorMessage: t("New quantity {{newQuantity}} is less than the combined quantity already claimed by all WBS items"),
    isDynamic: true,
    dynamicParams: ["newQuantity"],
  },
  // 18161-18163: the SAP bridge. When SAP DOES return text it is external copy, not a catalog
  // sentence, so those sites send UNEXPECTED_ERROR_WITH_MESSAGE with the text in `params`.
  CONTRACT_NOT_FOUND_PLAIN: {
    errorCode: 18161,
    errorMessage: t("Contract not found"),
    isDynamic: false,
  },
  CONTRACT_SAP_SYNC_NO_MESSAGE: {
    errorCode: 18162,
    errorMessage: t("SAP rejected the contract sync without returning a message."),
    isDynamic: false,
  },
  CONTRACT_SAP_MBOOK_NO_ACK: {
    errorCode: 18163,
    errorMessage: t("SAP did not acknowledge the M-Book posting."),
    isDynamic: false,
  },
  CONTRACT_SAP_SYNC_REJECTED_WITH_MESSAGES: {
    errorCode: 18164,
    errorMessage: t("SAP rejected the contract sync."),
    isDynamic: false,
  },
  // 18165-18168: contractsTimelineService. The last two ride a status:true response as informational notes, not refusals — the grid renders them beside an empty result.
  CONTRACT_TASK_PROGRESS_LOCKED_BY_ACTUAL_END: {
    errorCode: 18165,
    errorMessage: t("If Actual End Date is filled progress is auto filled to 100 which cannot be over ridden"),
    isDynamic: false,
  },
  CONTRACT_QUANTITY_INVALID_ADD: {
    errorCode: 18166,
    errorMessage: t("Invalid quantity cannot be added"),
    isDynamic: false,
  },
  CONTRACT_ID_REQUIRED: {
    errorCode: 18167,
    errorMessage: t("contractId is required"),
    isDynamic: false,
  },
  // 18168-18170: forms.contracts.service.ts.
  CONTRACT_MBOOK_REVERSAL_REJECTED_BY_SAP: {
    errorCode: 18168,
    errorMessage: t("The MBook reversal was rejected by SAP."),
    isDynamic: false,
  },
  CONTRACT_FORM_NOT_LINKED_PLAIN: {
    errorCode: 18169,
    errorMessage: t("The form is not linked to a contract."),
    isDynamic: false,
  },
  CONTRACT_BOQ_OPERATION_UNSUPPORTED: {
    errorCode: 18170,
    errorMessage: t('Unsupported BOQ operation "{{operation}}"'),
    isDynamic: true,
    dynamicParams: ["operation"],
  },
  // 18171: the updateContractTasks no-op guard.
  CONTRACT_MODIFY_NOTHING_TO_DO: {
    errorCode: 18171,
    errorMessage: t('Nothing to modify: entity must be "contract" with a contractId, or "tasks" with rows.'),
    isDynamic: false,
  },
  CONTRACT_ESTIMATE_NOT_PUBLISHED: {
    errorCode: 18172,
    errorMessage: t('The linked estimate no longer has a published version'),
    isDynamic: false,
  },
  CONTRACT_FILES_NOT_LINKABLE: {
    errorCode: 18173,
    errorMessage: t('Some files cannot be linked'),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
